import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Plus, Search, Filter, Download } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
// Server-relative upload URLs (/api/uploads/...) must be prefixed with the API origin,
// otherwise the browser hits the frontend host and nginx strips the wrong /api segment.
const toAbsUrl = (url) => url?.startsWith('/api/uploads/') ? `${API_BASE_URL}${url}` : (url || '');

const Campaigns = () => {
  const { isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [filteredCampaigns, setFilteredCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchCampaigns();
  }, []);

  useEffect(() => {
    filterCampaigns();
  }, [campaigns, searchQuery, statusFilter]);

  const fetchCampaigns = async () => {
    try {
      const response = await api.get('/campaigns');
      setCampaigns(response.data);
    } catch (error) {
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const filterCampaigns = () => {
    let filtered = campaigns;

    if (searchQuery) {
      filtered = filtered.filter(
        (c) =>
          c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          c.organization_name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter((c) => c.status === statusFilter);
    }

    setFilteredCampaigns(filtered);
  };

  // Calculate net revenue after 2.95% gateway fee
  const calculateNetRevenue = (totalRevenue) => {
    const gatewayFee = totalRevenue * 0.0295;
    return totalRevenue - gatewayFee;
  };

  // Download Excel for a campaign
  const handleDownloadExcel = async (campaign, e) => {
    e.stopPropagation(); // Prevent card click navigation
    try {
      const response = await api.get(`/campaigns/${campaign.id}/export/csv`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${campaign.slug}_registrations.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel exported successfully!');
    } catch (error) {
      toast.error('No registrations to export');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 font-jakarta">Loading events...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-100 sticky top-0 z-10 backdrop-blur-md bg-white/80">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-outfit font-bold text-slate-900">Events</h1>
              <p className="text-sm font-jakarta text-slate-600 mt-1">
                Manage all your event registrations
              </p>
            </div>
            {isSuperAdmin() && (
              <Button
                onClick={() => navigate('/campaigns/create')}
                data-testid="create-campaign-button"
                className="bg-primary hover:bg-primary-hover text-primary-foreground font-jakarta font-medium rounded-lg shadow-lg shadow-primary/20 transition-all duration-200 active:scale-95"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Event
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Filters */}
        <Card className="bg-white rounded-xl border border-slate-100 shadow-card p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Search events..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-campaigns-input"
                className="pl-10 h-11 rounded-lg border-slate-200 focus-visible:ring-primary transition-all duration-200"
              />
            </div>
            <div className="flex gap-2">
              {['all', 'draft', 'active', 'closed'].map((status) => (
                <Button
                  key={status}
                  variant={statusFilter === status ? 'default' : 'outline'}
                  onClick={() => setStatusFilter(status)}
                  data-testid={`filter-${status}-button`}
                  className={`capitalize font-jakarta rounded-lg transition-all duration-200 ${
                    statusFilter === status
                      ? 'bg-primary hover:bg-primary-hover text-white shadow-lg shadow-primary/20'
                      : 'border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  {status}
                </Button>
              ))}
            </div>
          </div>
        </Card>

        {/* Campaigns Grid */}
        {filteredCampaigns.length === 0 ? (
          <Card className="bg-white rounded-xl border border-slate-100 shadow-card p-12 text-center">
            <div className="max-w-md mx-auto">
              <Filter className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-outfit font-semibold text-slate-900 mb-2">No events found</h3>
              <p className="text-slate-600 font-jakarta mb-6">
                {searchQuery || statusFilter !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Get started by creating your first event'}
              </p>
              {isSuperAdmin() && (
                <Button
                  onClick={() => navigate('/campaigns/create')}
                  className="bg-primary hover:bg-primary-hover text-primary-foreground font-jakarta rounded-lg"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Event
                </Button>
              )}
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCampaigns.map((campaign) => (
              <Card
                key={campaign.id}
                onClick={() => navigate(`/campaigns/${campaign.id}`)}
                data-testid={`campaign-card-${campaign.id}`}
                className="bg-white rounded-xl border border-slate-100 shadow-card hover:shadow-md hover:border-primary/30 transition-all duration-300 cursor-pointer overflow-hidden group"
              >
                {/* Image Header */}
                <div className="h-32 bg-gradient-to-br from-primary to-secondary relative overflow-hidden">
                  <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-colors duration-300"></div>
                  {(campaign.logo_url || campaign.event_logo) && (
                    <img
                      src={toAbsUrl(campaign.logo_url || campaign.event_logo)}
                      alt={campaign.name}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>

                {/* Content */}
                <div className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-lg font-outfit font-semibold text-slate-900 group-hover:text-primary transition-colors duration-200">
                      {campaign.name}
                    </h3>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-jakarta font-medium ${
                        campaign.status === 'active'
                          ? 'bg-secondary-subtle text-secondary'
                          : campaign.status === 'draft'
                          ? 'bg-slate-100 text-slate-600'
                          : 'bg-accent-subtle text-accent'
                      }`}
                    >
                      {campaign.status}
                    </span>
                  </div>

                  <p className="text-sm font-jakarta text-slate-600 mb-2">
                    {campaign.organization_name}
                  </p>
                  
                  {/* Fund Collection Mode Badge */}
                  {campaign.fund_collection_mode && (
                    <div className="mb-4">
                      <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${
                        campaign.fund_collection_mode === 'kaizen_managed'
                          ? 'bg-violet-100 text-violet-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {campaign.fund_collection_mode === 'kaizen_managed' 
                          ? '💰 Kaizen Managed' 
                          : '🏦 Direct to Organizer'}
                      </span>
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                    <div>
                      <p className="text-xs font-jakarta text-slate-500">Registrations</p>
                      <p className="text-lg font-outfit font-bold text-slate-900">
                        {campaign.current_registrations}
                        {campaign.max_registrations && (
                          <span className="text-sm text-slate-500">/{campaign.max_registrations}</span>
                        )}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-jakarta text-slate-500">Revenue</p>
                      <p className="text-lg font-outfit font-bold text-slate-900">
                        ₹{calculateNetRevenue(campaign.total_revenue).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </p>
                    </div>
                  </div>

                  {/* Download Excel Button */}
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => handleDownloadExcel(campaign, e)}
                      className="w-full justify-center border-slate-200 hover:bg-slate-50 rounded-lg text-sm"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Excel
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Campaigns;
